/** Paint a ReceiptLayout to a canvas and hand it to the share sheet / download. */
import { FONT_FAMILY, type ReceiptLayout } from './layout';

export async function ensureFonts(): Promise<void> {
  if (typeof document === 'undefined' || !document.fonts) return;
  await Promise.all([
    document.fonts.load(`900 64px ${FONT_FAMILY.display}`),
    document.fonts.load(`600 64px ${FONT_FAMILY.body}`),
    document.fonts.load(`500 64px ${FONT_FAMILY.body}`),
    document.fonts.load(`500 48px ${FONT_FAMILY.mono}`),
  ]).catch(() => undefined);
}

export async function renderReceipt(layout: ReceiptLayout, canvas = document.createElement('canvas')): Promise<HTMLCanvasElement> {
  await ensureFonts();
  canvas.width = layout.width;
  canvas.height = layout.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2d canvas unavailable');
  ctx.fillStyle = layout.background;
  ctx.fillRect(0, 0, layout.width, layout.height);
  for (const op of layout.ops) {
    if (op.kind === 'rect') {
      ctx.fillStyle = op.color;
      ctx.fillRect(op.x, op.y, op.w, op.h);
    } else if (op.kind === 'dot') {
      ctx.beginPath();
      ctx.arc(op.x, op.y, op.r, 0, Math.PI * 2);
      if (op.filled) {
        ctx.fillStyle = op.color;
        ctx.fill();
      } else {
        ctx.strokeStyle = op.color;
        ctx.lineWidth = 4;
        ctx.stroke();
      }
    } else {
      ctx.font = `${op.weight} ${op.size}px ${FONT_FAMILY[op.font]}`;
      ctx.fillStyle = op.color;
      ctx.textAlign = op.align;
      ctx.textBaseline = 'alphabetic';
      ctx.fillText(op.text, op.x, op.y, op.align === 'left' ? layout.width - op.x - 80 : op.x - 80);
    }
  }
  return canvas;
}

export function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('toBlob failed'))), 'image/png'),
  );
}

/** One tap: native share sheet with the PNG when supported, else download. */
export async function shareOrDownload(blob: Blob, filename: string, text: string): Promise<'shared' | 'downloaded' | 'cancelled'> {
  const file = new File([blob], filename, { type: 'image/png' });
  const nav = navigator as Navigator & { canShare?: (d: ShareData) => boolean };
  if (nav.share && nav.canShare?.({ files: [file] })) {
    try {
      await nav.share({ files: [file], text });
      return 'shared';
    } catch (e) {
      if ((e as DOMException)?.name === 'AbortError') return 'cancelled';
    }
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
  return 'downloaded';
}
